import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { BASE_URL } from "../../utils/api";
import MainCard from "components/MainCard";
import {
  Alert, Box, Button, CircularProgress, FormControlLabel,
  Grid, IconButton, InputAdornment, MenuItem,
  Stack, Switch, TextField, Typography, Chip, Divider,
  useTheme, alpha,
} from "@mui/material";
import {
  ArrowLeftOutlined, CloudUploadOutlined,
  DeleteOutlined, PictureOutlined,
  CalendarOutlined, ClockCircleOutlined,
  ShopOutlined, OrderedListOutlined,
} from "@ant-design/icons";
import { useToast } from "contexts/ToastContext";
import brand from "utils/designTokens";
import { statusChipSx } from "components/table/tableChipStyles";

const brandSwitchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: brand.primary,
    "&:hover": { bgcolor: brand.overlay }
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    bgcolor: brand.primary,
    opacity: 1
  },
  "& .MuiSwitch-track": {
    bgcolor: "rgba(31, 91, 64, 0.22)",
    opacity: 1
  }
};

export default function BannerForm({ onSuccess, isModal = false }) {
  const navigate = useNavigate();
  const { showApiError } = useToast();
  const { id: paramId } = useParams();
  const id = isModal ? null : paramId; 
  const isEdit = Boolean(id);
  const theme    = useTheme();
  const primary  = theme.palette.primary.main;
  const primaryBg = alpha(primary, 0.06);

  const [form, setForm] = useState({
    store_id:  "",
    from_date: "",
    to_date:   "",
    from_time: "",
    to_time:   "",
    sequence:  "0",
    status:    true,
    is_main:   false,
    user_id:   "",
  });

  const [arFile,    setArFile]    = useState(null);
  const [enFile,    setEnFile]    = useState(null);
  const [arPreview, setArPreview] = useState(null);
  const [enPreview, setEnPreview] = useState(null);

  const [submitting,    setSubmitting]    = useState(false);
  const [loadingData,   setLoadingData]   = useState(false);
  const [stores,        setStores]        = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [errors,        setErrors]        = useState({});

  useEffect(() => {
    setStoresLoading(true);
    api.get("/store/list")
      .then((res) => setStores(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setStores([]))
      .finally(() => setStoresLoading(false));
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    setLoadingData(true);
    api.get(`/banner/${id}`)
      .then((res) => {
        const b = res.data?.data;
        if (!b) return;
setForm({
  store_id:  b.store_id  || "",
  from_date: b.from_date ? String(b.from_date).split("T")[0] : "",
  to_date:   b.to_date   ? String(b.to_date).split("T")[0]   : "",

  from_time: b.from_time ? String(b.from_time).slice(0, 5) : "",
  to_time:   b.to_time   ? String(b.to_time).slice(0, 5)   : "",

  sequence:  String(b.sequence ?? 0),
  status:    !!b.status,
  is_main:   !!b.is_main,
  user_id:   b.user_id || "",
});
        if (b.imageAr?.attachment_url) setArPreview(toImgUrl(b.imageAr.attachment_url));
        if (b.imageEn?.attachment_url) setEnPreview(toImgUrl(b.imageEn.attachment_url));
      })
      .catch((err) => showApiError(err, "Failed to load banner"))
      .finally(() => setLoadingData(false));
  }, [id]);

const toImgUrl = (path) => {
  if (!path) return null;
  return path.startsWith("http") ? path : `${BASE_URL}/${path}`;
};

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    setErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleArFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setArFile(file);
    setArPreview(URL.createObjectURL(file));
    setErrors((p) => ({ ...p, image_ar: "" }));
  };

  const handleEnFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setEnFile(file);
    setEnPreview(URL.createObjectURL(file));
    setErrors((p) => ({ ...p, image_en: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.from_date) e.from_date = "From date is required";
    if (!form.to_date)   e.to_date   = "To date is required";
    if (form.from_date && form.to_date) {
      const start = new Date(`${form.from_date}T${form.from_time || "00:00"}:00`);
      const end   = new Date(`${form.to_date}T${form.to_time     || "23:59"}:00`);
      if (start >= end) e.to_time = "End date/time must be after start date/time";
    }
    if (!isEdit && !arFile && !enFile)
      e.image_ar = "At least one image (AR or EN) is required";
    if (isNaN(Number(form.sequence)) || Number(form.sequence) < 0)
      e.sequence = "Sequence must be a non-negative number";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      if (form.store_id) fd.append("store_id", form.store_id);
      if (form.user_id)  fd.append("user_id",  form.user_id);
      fd.append("from_date", form.from_date);
      fd.append("to_date",   form.to_date);
fd.append("from_time", form.from_time ? `${form.from_time}:00` : "");
fd.append("to_time",   form.to_time   ? `${form.to_time}:00`   : "");
      fd.append("sequence",  form.sequence);
      fd.append("status",    form.status);
      fd.append("is_main",   form.is_main);
      if (arFile) fd.append("image_ar", arFile);
      if (enFile) fd.append("image_en", enFile);

      if (isEdit) {
        await api.put(`/banner/edit/${id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        await api.post("/banner/add", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }

      if (onSuccess) {
        onSuccess();
      } else {
        navigate("/banner/list");
      }
    } catch (err) {
      showApiError(err, "Failed to save banner");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // ── Image upload card 
  const ImageUploadCard = ({ title, lang, preview, file, onFile, onClear, error, isMain }) => (
    <MainCard
      sx={{
        border: "1px solid",
        borderColor: error ? "error.main" : "divider",
        borderRadius: 2,
        height: "100%",
      }}
      title={
        <Stack direction="row" alignItems="center" gap={1}>
          <PictureOutlined style={{ color: primary }} />
          <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
          <Chip label={lang} size="small" color="primary" sx={{ fontWeight: 700, fontSize: "0.68rem" }} />
        </Stack>
      }
    >
      {/* Preview  */}
      <Box sx={{
        width: "100%",
        aspectRatio: isMain ? "2 / 1" : "2.7 / 1",
        height: "auto",
        border: "2px dashed",
        borderColor: error ? "error.main" : preview ? "primary.main" : "grey.300",
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: preview ? "grey.900" : "grey.50",
        mb: 2,
        transition: "border-color 0.2s, background 0.2s",
        "&:hover": { borderColor: "primary.main", bgcolor: preview ? "grey.900" : primaryBg },
      }}>
        {preview ? (
          <Box
            component="img"
            src={preview}
            alt={`${lang} preview`}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <Stack alignItems="center" spacing={1} sx={{ color: "text.disabled" }}>
            <PictureOutlined style={{ fontSize: 44 }} />
            <Typography variant="caption" fontWeight={500}>No image selected</Typography>
            <Typography variant="caption" fontSize="0.67rem">
              {isMain ? "Recommended: 1080 × 540 px (2:1)" : "Recommended: 1080 × 400 px (2.7:1)"}
            </Typography>
          </Stack>
        )}
      </Box>

      <Stack direction="row" spacing={1}>
        <Button
          variant={preview ? "outlined" : "contained"}
          component="label"
          fullWidth
          color="primary"
          startIcon={<CloudUploadOutlined />}
        >
          {file
            ? (file.name.length > 22 ? file.name.slice(0, 22) + "…" : file.name)
            : preview ? "Replace Image" : "Upload Image"}
          <input type="file" hidden accept="image/*" onChange={onFile} />
        </Button>
        {preview && (
          <IconButton
            size="small"
            color="error"
            onClick={onClear}
            sx={{ border: "1px solid", borderColor: "error.light" }}
          >
            <DeleteOutlined />
          </IconButton>
        )}
      </Stack>

      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.75, display: "block" }}>
          {error}
        </Typography>
      )}
      <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: "block" }}>
        JPG, PNG, WEBP — max recommended 2 MB
      </Typography>
    </MainCard>
  );

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>

      {/* ── Page Header ──────────────────────────────────────────────────────── */}
      {!isModal && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" gap={1.5}>
            <IconButton
              size="small"
              onClick={() => navigate(-1)}
              sx={{ bgcolor: "grey.100", "&:hover": { bgcolor: "grey.200" } }}
            >
              <ArrowLeftOutlined />
            </IconButton>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                {isEdit ? "Edit Banner" : "Add Banner"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isEdit
                  ? "Update banner details and schedule"
                  : "Create a new promotional banner with schedule"}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" gap={1}>
            <Button variant="outlined" onClick={() => navigate(-1)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              color="primary"
              disabled={submitting}
              sx={{ px: 3 }}
            >
              {submitting ? (
                <><CircularProgress size={16} sx={{ mr: 1, color: "inherit" }} />Saving…</>
              ) : isEdit ? "Update Banner" : "Create Banner"}
            </Button>
          </Stack>
        </Stack>
      )}

      <Grid container spacing={3}>

        {/* Store & Display */}
        <Grid size={{xs:12, md:6}}>
          <MainCard
            sx={{ height: "100%" }}
            title={
              <Stack direction="row" alignItems="center" gap={1}>
                <ShopOutlined style={{ color: primary }} />
                <Typography variant="subtitle1" fontWeight={600}>Store & Display</Typography>
              </Stack>
            }
          >
            <Grid container spacing={2.5}>

              {/* Store selector */}
              <Grid size={{xs:12}}>
                <TextField
                  select fullWidth
                  label="Store (optional)"
                  name="store_id"
                  value={form.store_id}
                  onChange={handleChange}
                  helperText="Leave blank to show on all stores (global banner)"
                  disabled={storesLoading}
                  InputProps={storesLoading ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <CircularProgress size={16} />
                      </InputAdornment>
                    ),
                  } : undefined}
                >
                  <MenuItem value=""><em>All Stores (Global)</em></MenuItem>
                  {stores.map((s) => (
                    <MenuItem key={s.store_id} value={s.store_id}>{s.title_en}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Sequence */}
              <Grid size={{xs:12, sm:6}}>
                <TextField
                  fullWidth required
                  label="Sequence"
                  name="sequence"
                  type="number"
                  value={form.sequence}
                  onChange={handleChange}
                  inputProps={{ min: 0 }}
                  error={!!errors.sequence}
                  helperText={errors.sequence || "Display order (lower = first)"}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <OrderedListOutlined style={{ color: "#aaa" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Status toggle */}
              <Grid size={{xs:12}}>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{
                  px: 2, py: 1.5,
                  borderRadius: 2,
                  bgcolor: form.status ? brand.overlay : "grey.100",
                  border: "1px solid",
                  borderColor: form.status ? brand.borderFocus : "divider",
                  transition: "all 0.2s",
                }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Banner Status</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {form.status
                          ? "Visible in app (within schedule window)"
                          : "Hidden from app regardless of schedule"}
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.status}
                          onChange={(e) => setForm((p) => ({ ...p, status: e.target.checked }))}
                          sx={brandSwitchSx}
                        />
                      }
                      label={
                        <Chip
                          label={form.status ? "Active" : "Inactive"}
                          size="small"
                          sx={statusChipSx(form.status)}
                        />
                      }
                      labelPlacement="start"
                      sx={{ ml: 0, gap: 1 }}
                    />
                  </Stack>
                </Box>
              </Grid>

              {/* Main toggle */}
              <Grid size={{xs:12}}>
                <Box sx={{
                  px: 2, py: 1.5,
                  borderRadius: 2,
                  bgcolor: form.is_main ? brand.overlay : "grey.100",
                  border: "1px solid",
                  borderColor: form.is_main ? brand.borderFocus : "divider",
                  transition: "all 0.2s",
                }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Main Banner</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {form.is_main
                          ? "This banner is marked as a main banner"
                          : "This is a regular banner"}
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.is_main}
                          onChange={(e) => setForm((p) => ({ ...p, is_main: e.target.checked }))}
                          sx={brandSwitchSx}
                        />
                      }
                      label={
                        <Chip
                          label={form.is_main ? "Yes" : "No"}
                          size="small"
                          sx={statusChipSx(form.is_main)}
                        />
                      }
                      labelPlacement="start"
                      sx={{ ml: 0, gap: 1 }}
                    />
                  </Stack>
                </Box>
              </Grid>

            </Grid>
          </MainCard>
        </Grid>

        {/* Schedule */}
        <Grid size={{xs:12, md:6}}>
          <MainCard
            sx={{ height: "100%" }}
            title={
              <Stack direction="row" alignItems="center" gap={1}>
                <CalendarOutlined style={{ color: primary }} />
                <Typography variant="subtitle1" fontWeight={600}>Schedule</Typography>
                <Chip
                  label="Date + Time Window"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontSize: "0.64rem" }}
                />
              </Stack>
            }
          >
            <Grid container spacing={2.5}>

              {/* From Date */}
              <Grid size={{xs:12, sm:6}}>
                <TextField
                  fullWidth required
                  label="From Date"
                  name="from_date"
                  type="date"
                  value={form.from_date}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.from_date}
                  helperText={errors.from_date || "Banner start date"}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarOutlined style={{ color: "#aaa" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* From Time */}
              <Grid size={{xs:12, sm:6}}>
                <TextField
                  fullWidth
                  label="From Time (optional)"
                  name="from_time"
                  type="time"
                  value={form.from_time}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  helperText="Default: 00:00 (start of day)"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ClockCircleOutlined style={{ color: "#aaa" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* To Date */}
              <Grid size={{xs:12, sm:6}}>
                <TextField
                  fullWidth required
                  label="To Date"
                  name="to_date"
                  type="date"
                  value={form.to_date}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: form.from_date || undefined }}
                  error={!!errors.to_date}
                  helperText={errors.to_date || "Banner end date"}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarOutlined style={{ color: "#aaa" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* To Time */}
              <Grid size={{xs:12, sm:6}}>
                <TextField
                  fullWidth
                  label="To Time (optional)"
                  name="to_time"
                  type="time"
                  value={form.to_time}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.to_time}
                  helperText={errors.to_time || "Default: 23:59 (end of day)"}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ClockCircleOutlined style={{ color: "#aaa" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Live window preview */}
              {form.from_date && form.to_date && (
                <Grid size={{xs:12}}>
                  <Box sx={{
                    px: 2, py: 1.5,
                    borderRadius: 2,
                    bgcolor: primaryBg,
                    border: "1px dashed",
                    borderColor: "primary.light",
                  }}>
                    <Typography variant="caption" color="primary.main" fontWeight={600}>
                      📅 Active Window:&nbsp;
                      {form.from_date} {form.from_time || "00:00"}
                      &nbsp;→&nbsp;
                      {form.to_date} {form.to_time || "23:59"}
                    </Typography>
                  </Box>
                </Grid>
              )}

            </Grid>
          </MainCard>
        </Grid>

        {/* Banner Images (AR & EN) moved lower */}
        <Grid size={{xs:12, md:6}}>
          <ImageUploadCard
            title="Banner Image (Arabic)"
            lang="AR"
            preview={arPreview}
            file={arFile}
            onFile={handleArFile}
            onClear={() => { setArFile(null); setArPreview(null); }}
            error={errors.image_ar}
            isMain={form.is_main}
          />
        </Grid>

        <Grid size={{xs:12, md:6}}>
          <ImageUploadCard
            title="Banner Image (English)"
            lang="EN"
            preview={enPreview}
            file={enFile}
            onFile={handleEnFile}
            onClear={() => { setEnFile(null); setEnPreview(null); }}
            error={errors.image_en}
            isMain={form.is_main}
          />
        </Grid>

      </Grid>

      {isModal && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button 
            variant="outlined" 
            color="secondary" 
            onClick={() => onSuccess && onSuccess(false)} 
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            color="primary"
            disabled={submitting}
          >
            {submitting ? (
              <><CircularProgress size={16} sx={{ mr: 1, color: "inherit" }} />Saving…</>
            ) : isEdit ? "Update Banner" : "Create Banner"}
          </Button>
        </Box>
      )}
    </Box>
  );
}